"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import useToggleState from "@lib/hooks/use-toggle-state"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Text, clx } from "@modules/common/components/ui"
import { Fragment } from "react"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { Locale } from "@lib/data/locales"


const SideMenuItems = [
  { name: "Inicio", href: "/" },
  { name: "Mi cuenta", href: "/account" },
  { name: "Carrito", href: "/cart" },
]


type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  categories: HttpTypes.StoreProductCategory[]
}

const SideMenu = ({
  regions,
  locales,
  currentLocale,
  categories,
}: SideMenuProps) => {
const countryToggleState = useToggleState()
const languageToggleState = useToggleState()

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className="relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none hover:text-ui-fg-base"
                >
                  <div className="flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 hover:bg-gray-100 transition xl:gap-2 xl:px-4 xl:py-2">
  <span className="text-lg xl:text-xl">☰</span>
  <span className="text-xs font-semibold uppercase tracking-wide xl:text-base">
    Menú
  </span>
</div>
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-black/0 pointer-events-auto"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <PopoverPanel className="flex flex-col absolute w-full pr-4 sm:pr-0 sm:w-1/3 2xl:w-1/4 sm:min-w-min h-[calc(100vh-1rem)] z-[51] inset-x-0 text-sm text-ui-fg-on-color m-2 backdrop-blur-2xl">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex flex-col h-full bg-black rounded-2xl justify-between p-8 shadow-2xl"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button data-testid="close-menu-button" onClick={close}>
                        <XMark />
                      </button>
                    </div>
                    <ul className="flex flex-col gap-6 items-start justify-start">
                      {SideMenuItems.slice(0, 1).map(({ name, href }) => (
  <li key={name}>
    <LocalizedClientLink
      href={href}
      className="text-lg font-bold text-white hover:text-yellow-400 transition"
      onClick={close}
      data-testid={`${name.toLowerCase()}-link`}
    >
      {name}
    </LocalizedClientLink>
  </li>
))}

{categories.map((category) => (
  <li key={category.id}>
    <LocalizedClientLink
      href={`/categories/${encodeURIComponent(category.handle)}`}
      className="text-lg font-bold text-white hover:text-yellow-400 transition"
      onClick={close}
      data-testid={`${category.handle}-link`}
    >
      {category.name}
    </LocalizedClientLink>
  </li>
))}

{SideMenuItems.slice(1).map(({ name, href }) => (
  <li key={name}>
    <LocalizedClientLink
      href={href}
      className="text-lg font-bold text-white hover:text-yellow-400 transition"
      onClick={close}
      data-testid={`${name.toLowerCase()}-link`}
    >
      {name}
    </LocalizedClientLink>
  </li>
))}
                  </ul>
                    <div className="flex flex-col gap-y-6">
                      {!!locales?.length && (
                        <div
                          className="flex justify-between"
                          onMouseEnter={languageToggleState.open}
                          onMouseLeave={languageToggleState.close}
                        >
                          <LanguageSelect
                            toggleState={languageToggleState}
                            locales={locales}
                            currentLocale={currentLocale}
                          />
                          <ArrowRightMini
                            className={clx(
                              "transition-transform duration-150",
                              languageToggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                      )}
                      <div
                        className="flex justify-between"
                        onMouseEnter={countryToggleState.open}
                        onMouseLeave={countryToggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={countryToggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "transition-transform duration-150",
                            countryToggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>
                      <Text as="div" className="flex justify-between txt-compact-small">
  <Text as="span" className="text-sm text-gray-400">
    © {new Date().getFullYear()} LEGENDARY STORE
  </Text>
  <span>reserved.</span>
</Text>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
